import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "../layout/Layout";
import { services, projects } from "../data/siteData";
import AboutSection from "../sections/AboutSection";
import TeamSection from "../sections/TeamSection";
import ContactSection from "../sections/ContactSection";
import TestimonialsSection from "../sections/TestimonialsSection";
import { Button } from "../Components/ui/Button";
import { Input } from "../Components/ui/Input";
import { Badge } from "../Components/ui/Badge";
import { Slider } from "../Components/ui/Slider";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  ArrowRight, Layout as LayoutIcon, Globe, Smartphone, Cpu, Database,
  ShieldCheck, CheckCircle2, Zap, Clock, MessageSquare,
  Code, Award, BarChart3, Activity, Users, CreditCard, Mail
} from "lucide-react";

const iconMap = {
  Layout: LayoutIcon,
  Globe: Globe,
  Smartphone: Smartphone,
  Cpu: Cpu,
  Database: Database,
  ShieldCheck: ShieldCheck
};

function AnimatedCounter({ value, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = value;
      const totalMiliseconds = duration * 1000;
      const incrementTime = 16; 
      const steps = totalMiliseconds / incrementTime;
      const increment = end / steps;

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.ceil(start));
        }
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
}

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

function serviceSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

const words = ["Web Apps", "Mobile Apps", "AI Products", "Web3 Systems"];

const projectTypes = [
  { id: 0, title: "Website / Marketing Site", desc: "Corporate sites, landing pages", basePrice: 8000, baseWeeks: 4, icon: LayoutIcon },
  { id: 1, title: "Web App / SaaS", desc: "Complex web platforms", basePrice: 25000, baseWeeks: 10, icon: Globe },
  { id: 2, title: "Mobile App", desc: "iOS & Android apps", basePrice: 35000, baseWeeks: 12, icon: Smartphone },
  { id: 3, title: "AI / ML Product", desc: "LLMs, data pipelines", basePrice: 45000, baseWeeks: 14, icon: Cpu },
];

const featureOptions = [
  { id: "auth", label: "User authentication", price: 3000, weeks: 1 },
  { id: "payments", label: "Payments / Stripe", price: 5000, weeks: 1.5 },
  { id: "admin", label: "Admin dashboard", price: 6000, weeks: 2 },
  { id: "realtime", label: "Real-time / WebSockets", price: 5000, weeks: 1.5 },
  { id: "ai", label: "AI features / LLM", price: 8000, weeks: 2 },
  { id: "api", label: "Third-party APIs", price: 4000, weeks: 1 },
  { id: "analytics", label: "Analytics & reporting", price: 3000, weeks: 1 },
  { id: "i18n", label: "Multi-language / i18n", price: 3000, weeks: 1 },
];

const designOptions = [
  { id: 0, label: "Standard", multiplier: 1.0 },
  { id: 1, label: "Premium", multiplier: 1.3 },
  { id: 2, label: "Bespoke", multiplier: 1.6 },
];

const urgencyOptions = [
  { id: 0, label: "Standard", costMult: 1.0, weeksMult: 1.0 },
  { id: 1, label: "Fast Track", costMult: 1.25, weeksMult: 0.75 },
  { id: 2, label: "Rush", costMult: 1.6, weeksMult: 0.55 },
];

export default function HomePage() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  // Estimator State
  const [estimatorProjectType, setEstimatorProjectType] = useState(0);
  const [estimatorPlatforms, setEstimatorPlatforms] = useState(['iOS']);
  const [estimatorFeatures, setEstimatorFeatures] = useState([]);
  const [estimatorDesign, setEstimatorDesign] = useState(0);
  const [estimatorTeamSize, setEstimatorTeamSize] = useState([3]);
  const [estimatorUrgency, setEstimatorUrgency] = useState(0);

  const { estimatedTotal, estimatedWeeks } = useMemo(() => {
    const pType = projectTypes[estimatorProjectType];
    const basePrice = pType.basePrice;
    const baseWeeks = pType.baseWeeks;

    let featuresPrice = 0;
    let featuresWeeks = 0;
    estimatorFeatures.forEach(fId => {
      const f = featureOptions.find(opt => opt.id === fId);
      if (f) {
        featuresPrice += f.price;
        featuresWeeks += f.weeks;
      }
    });

    let platformMult = 1.0;
    if (estimatorProjectType === 2) {
      if (estimatorPlatforms.length === 1) platformMult = 1.0;
      else if (estimatorPlatforms.length === 2) platformMult = 1.6;
      else if (estimatorPlatforms.length === 3) platformMult = 1.9;
      else platformMult = 1.0; // fallback if empty
    }

    const designMult = designOptions[estimatorDesign].multiplier;
    const urg = urgencyOptions[estimatorUrgency];
    const teamSize = estimatorTeamSize[0];

    const total = (basePrice + featuresPrice) * platformMult * designMult * urg.costMult * (1 + 0.08 * (teamSize - 1));
    const weeks = (baseWeeks + featuresWeeks) * urg.weeksMult * Math.max(0.5, 1 - 0.06 * (teamSize - 1));

    // round to 500
    return {
      estimatedTotal: Math.round(total / 500) * 500,
      estimatedWeeks: Math.round(weeks),
    };
  }, [estimatorProjectType, estimatorPlatforms, estimatorFeatures, estimatorDesign, estimatorTeamSize, estimatorUrgency]);

  const toggleFeature = (id) => {
    setEstimatorFeatures(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const togglePlatform = (id) => {
    setEstimatorPlatforms(prev => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev; // prevent empty
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      title: "Discover",
      desc: "Deep dive into your business goals, user needs, and technical constraints.",
      deliverables: ["Product Strategy", "Technical Architecture", "User Research", "Project Roadmap"],
      timeline: "1-2 Weeks",
      team: "Product Manager, Tech Lead, UX Researcher"
    },
    {
      title: "Design",
      desc: "Crafting beautiful, intuitive interfaces that solve complex user problems.",
      deliverables: ["Wireframes", "High-fidelity UI", "Interactive Prototypes", "Design System"],
      timeline: "2-3 Weeks",
      team: "UX Designer, UI Designer, Product Manager"
    },
    {
      title: "Build",
      desc: "Agile engineering sprints delivering production-ready code you can test.",
      deliverables: ["Frontend App", "Backend API", "Database Schema", "CI/CD Pipeline"],
      timeline: "4-8 Weeks",
      team: "Frontend Eng, Backend Eng, DevOps"
    },
    {
      title: "Launch",
      desc: "Rigorous QA, final polish, and smooth deployment to production.",
      deliverables: ["QA Report", "Performance Audit", "Deployment", "Handoff Docs"],
      timeline: "1-2 Weeks",
      team: "QA Engineer, Tech Lead, DevOps"
    }
  ];

  return (
    <Layout>
      {/* 1. Hero */}
      <section id="home" className="relative pt-32 pb-24 overflow-hidden bg-[#FAFAFB]">
        <div className="absolute top-28 right-0 w-[700px] h-[700px] bg-[#6F00FF]/5 rounded-full blur-[120px] translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <Badge variant="secondary" className="mb-8 bg-white border border-zinc-200 text-[#0B0B16] font-medium rounded-full px-4 py-1.5 shadow-sm">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#6F00FF] animate-pulse" />
                Now accepting new projects for Q3
              </span>
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-extrabold leading-[1.1] mb-6 text-[#0B0B16] tracking-tight">
              Software, engineered to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6F00FF] to-[#9333EA]">scale.</span>
            </h1>
            
            <div className="text-2xl md:text-3xl font-display font-medium text-[#5A5A6E] mb-8 h-10 flex items-center gap-2">
              We build
              <div className="relative inline-block w-[240px]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={currentWordIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="absolute left-0 text-[#0B0B16] font-bold"
                  >
                    {words[currentWordIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>

            <p className="text-lg text-[#5A5A6E] mb-10 font-body leading-relaxed max-w-lg">
              From complex SaaS platforms to immersive mobile apps, we turn bold ideas into flawless execution with elite engineering teams.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="bg-[#6F00FF] hover:bg-[#5D21D2] text-white h-14 px-8 rounded-full text-base font-semibold shadow-[0_8px_30px_rgb(111,0,255,0.2)] group transition-all"
              >
                Start a Project <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => scrollToSection("work")}
                className="h-14 px-8 rounded-full text-base font-semibold border-zinc-200 bg-white hover:bg-zinc-50 text-[#0B0B16] shadow-sm"
              >
                View our work
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="relative lg:block hidden"
          >
            {/* Mockup Container */}
            <div className="relative z-10 w-full rounded-2xl bg-white border border-zinc-200 shadow-[0_20px_60px_rgb(0,0,0,0.05)] overflow-hidden flex flex-col h-[500px]">
              {/* Fake Window Header */}
              <div className="h-12 border-b border-zinc-100 bg-zinc-50/50 flex items-center px-4 gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <div className="w-48 border-r border-zinc-100 bg-zinc-50/30 p-4 flex flex-col gap-2">
                  <div className="w-24 h-4 bg-zinc-200 rounded animate-pulse mb-6" />
                  {[LayoutIcon, Activity, Users, CreditCard].map((Icon, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${i === 1 ? 'bg-[#6F00FF]/10 text-[#6F00FF]' : 'text-zinc-400'}`}>
                      <Icon size={16} />
                      <div className={`w-16 h-2 rounded ${i === 1 ? 'bg-[#6F00FF]/40' : 'bg-zinc-200'}`} />
                    </div>
                  ))}
                </div>
                {/* Main Content */}
                <div className="flex-1 p-6 bg-white flex flex-col gap-6">
                  <div className="flex justify-between items-center">
                    <div className="w-32 h-6 bg-zinc-100 rounded" />
                    <div className="w-8 h-8 rounded-full bg-zinc-100" />
                  </div>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                      <div className="w-12 h-3 bg-zinc-200 rounded mb-3" />
                      <div className="text-2xl font-bold text-[#0B0B16]">$124,500</div>
                      <div className="text-xs text-green-500 mt-1 font-medium">+14.2%</div>
                    </div>
                    <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                      <div className="w-16 h-3 bg-zinc-200 rounded mb-3" />
                      <div className="text-2xl font-bold text-[#0B0B16]">14,230</div>
                      <div className="text-xs text-green-500 mt-1 font-medium">+5.1%</div>
                    </div>
                  </div>
                  {/* Chart Fake */}
                  <div className="flex-1 rounded-xl border border-zinc-100 bg-zinc-50/50 p-4 flex items-end gap-2">
                    {[40, 70, 45, 90, 65, 100, 85].map((h, i) => (
                      <motion.div 
                        key={i} 
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{ duration: 1, delay: i * 0.1 }}
                        className="flex-1 bg-[#6F00FF]/20 rounded-t-sm"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badges */}
            <motion.div 
              animate={{ y: [-10, 10, -10] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute -right-6 top-24 bg-white rounded-xl shadow-lg border border-zinc-100 p-3 flex items-center gap-3 z-20"
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-[#0B0B16]">Sample dashboard UI</div>
                <div className="text-xs text-zinc-500">Portfolio preview</div>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [10, -10, 10] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
              className="absolute -left-8 bottom-32 bg-white rounded-xl shadow-lg border border-zinc-100 p-3 flex items-center gap-3 z-20"
            >
              <div className="w-10 h-10 rounded-full bg-[#6F00FF]/10 flex items-center justify-center text-[#6F00FF]">
                <BarChart3 size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-[#0B0B16]">Analytics concept</div>
                <div className="text-xs text-zinc-500">Representative build</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stack Strip */}
        <div className="mt-24 border-t border-zinc-200/50 bg-white/50 backdrop-blur-sm py-8 relative z-10">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-sm font-semibold text-zinc-400 uppercase tracking-widest mb-6">Technologies we build with</p>
            <div className="flex overflow-hidden relative w-full mask-edges">
              <motion.div 
                className="flex gap-8 items-center whitespace-nowrap min-w-max"
                animate={{ x: ["0%", "-50%"] }}
                transition={{ repeat: Infinity, ease: "linear", duration: 30 }}
              >
                {["React", "Next.js", "TypeScript", "Node.js", "Python", "React Native", "Flutter", "AWS", "PostgreSQL", "TensorFlow", "Solidity", "React", "Next.js", "TypeScript", "Node.js", "Python", "React Native", "Flutter", "AWS", "PostgreSQL", "TensorFlow", "Solidity"].map((tech, i) => (
                  <div key={i} className="px-5 py-2 rounded-full border border-zinc-200 bg-white text-sm font-semibold text-zinc-500 select-none">
                    {tech}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Honest stats band */}
      <section className="py-16 bg-[#FAFAFB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {[
              { value: 6, suffix: "", label: "In-house team members" },
              { value: 6, suffix: "", label: "Core service areas" },
              { display: "<24h", label: "Typical response time" },
              { display: "100%", label: "Code & IP ownership" },
            ].map((stat, i) => (
              <div key={i} className="text-center px-4">
                <div className="text-5xl md:text-6xl font-display font-black mb-2 text-[#0B0B16]">
                  {stat.display ? (
                    stat.display
                  ) : (
                    <>
                      <AnimatedCounter value={stat.value} duration={2.5} />
                      {stat.suffix}
                    </>
                  )}
                </div>
                <p className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Why Quantum Spark Technology */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#0B0B16] mb-6">Why Quantum Spark Technology?</h2>
            <p className="text-lg text-[#5A5A6E] font-body">We don't just write code. We act as your strategic technical partners, ensuring every architectural decision serves your business goals.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large Card 1 */}
            <div className="md:col-span-2 bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.1)] transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center text-[#0B0B16] mb-6 group-hover:scale-110 transition-transform shadow-sm">
                <Zap size={24} />
              </div>
              <h3 className="text-2xl font-display font-bold text-[#0B0B16] mb-3">Ship in weeks, not quarters</h3>
              <p className="text-[#5A5A6E]">Our optimized workflow and component libraries allow us to deliver production-ready MVPs in typically 6-8 weeks without compromising on architecture.</p>
              <div className="mt-8 flex gap-2">
                <div className="h-2 w-1/4 bg-[#6F00FF] rounded-full" />
                <div className="h-2 w-1/6 bg-zinc-200 rounded-full" />
                <div className="h-2 w-1/12 bg-zinc-200 rounded-full" />
              </div>
            </div>

            {/* Small Card 1 */}
            <div className="bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.1)] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-white border border-zinc-200 flex items-center justify-center text-[#0B0B16] mb-6 group-hover:scale-110 transition-transform shadow-sm">
                <Award size={20} />
              </div>
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2">Senior-led delivery</h3>
              <p className="text-sm text-[#5A5A6E]">Leadership stays hands-on in architecture and quality, with delivery support to keep projects moving.</p>
            </div>

            {/* Small Card 2 */}
            <div className="bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.1)] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-white border border-zinc-200 flex items-center justify-center text-[#0B0B16] mb-6 group-hover:scale-110 transition-transform shadow-sm">
                <MessageSquare size={20} />
              </div>
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2">24/7 dedicated Slack</h3>
              <p className="text-sm text-[#5A5A6E]">Direct access to your entire team. Complete transparency.</p>
            </div>

            {/* Small Card 3 */}
            <div className="bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.1)] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-white border border-zinc-200 flex items-center justify-center text-[#0B0B16] mb-6 group-hover:scale-110 transition-transform shadow-sm">
                <Code size={20} />
              </div>
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2">Code you fully own</h3>
              <p className="text-sm text-[#5A5A6E]">No vendor lock-in. Full IP rights and clean handoff.</p>
            </div>

            {/* Large Card 2 */}
            <div className="md:col-span-2 bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.1)] transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-[#6F00FF]/10 text-[#6F00FF] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm">
                <CreditCard size={24} />
              </div>
              <h3 className="text-2xl font-display font-bold text-[#0B0B16] mb-3">Transparent fixed pricing</h3>
              <p className="text-[#5A5A6E]">No surprise bills or endless hourly logging. We scope the project, agree on deliverables, and stick to the price.</p>
            </div>
          </div>
        </div>
      </section>

      <AboutSection />

      {/* 4. Services preview */}
      <section id="services" className="py-24 bg-[#FAFAFB] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#0B0B16] mb-6">Capabilities that scale</h2>
            <p className="text-[#5A5A6E] text-lg font-body">Comprehensive engineering and design services tailored to your most ambitious goals.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon] || LayoutIcon;
              const gradients = [
                "from-blue-500/10", "from-purple-500/10", "from-pink-500/10", 
                "from-emerald-500/10", "from-amber-500/10", "from-indigo-500/10"
              ];
              return (
                <Link key={i} to={`/services#${serviceSlug(service.title)}`} className="block">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden border border-zinc-200 hover:-translate-y-1 hover:shadow-xl hover:border-[#6F00FF]/30 transition-all duration-300 group cursor-pointer flex flex-col h-full"
                >
                  <div className={`h-24 bg-gradient-to-b ${gradients[i]} to-transparent relative`}>
                    <div className="absolute -bottom-6 left-8 w-16 h-16 rounded-2xl bg-white border border-zinc-200 shadow-sm flex items-center justify-center text-[#6F00FF] group-hover:scale-110 transition-transform">
                      <Icon size={24} />
                    </div>
                  </div>
                  <div className="pt-10 pb-8 px-8 flex-1 flex flex-col">
                    <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-3">{service.title}</h3>
                    <p className="text-[#5A5A6E] mb-6 text-sm flex-1">{service.shortDesc}</p>
                    <ul className="space-y-2 mb-8">
                      {service.bullets.slice(0,3).map((bullet, idx) => (
                        <li key={idx} className="text-sm font-medium text-zinc-600 flex items-center gap-2">
                          <CheckCircle2 size={14} className="text-[#6F00FF]" /> {bullet}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-[#6F00FF] font-semibold text-sm group-hover:gap-2 transition-all">
                      Learn more <ArrowRight size={16} className="ml-1" />
                    </div>
                  </div>
                </motion.div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* 5. Interactive "How we work" timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#0B0B16] mb-6">How we work</h2>
            <p className="text-lg text-[#5A5A6E] font-body max-w-2xl">A battle-tested methodology for delivering excellence. Predictable, transparent, and built for speed.</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            {/* Timeline Nav */}
            <div className="lg:w-1/3 flex flex-col gap-4">
              {steps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => setActiveStep(i)}
                  className={`text-left p-6 rounded-2xl border transition-all ${
                    activeStep === i 
                      ? "bg-white border-[#6F00FF] shadow-lg" 
                      : "bg-[#FAFAFB] border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <div className="flex items-center gap-4 mb-2">
                    <span className={`font-display font-black text-xl ${activeStep === i ? 'text-[#6F00FF]' : 'text-zinc-400'}`}>
                      0{i+1}
                    </span>
                    <h3 className={`font-display font-bold text-xl ${activeStep === i ? 'text-[#0B0B16]' : 'text-zinc-500'}`}>
                      {step.title}
                    </h3>
                  </div>
                </button>
              ))}
            </div>

            {/* Timeline Content */}
            <div className="lg:w-2/3 bg-[#FAFAFB] rounded-3xl p-8 md:p-12 border border-zinc-200 flex items-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="w-full"
                >
                  <h3 className="text-3xl font-display font-bold text-[#0B0B16] mb-4">{steps[activeStep].title} Phase</h3>
                  <p className="text-lg text-[#5A5A6E] mb-8">{steps[activeStep].desc}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-[#6F00FF]" /> Key Deliverables
                      </h4>
                      <ul className="space-y-3">
                        {steps[activeStep].deliverables.map((item, idx) => (
                          <li key={idx} className="text-[#5A5A6E] text-sm flex items-center gap-2 before:content-[''] before:w-1.5 before:h-1.5 before:bg-zinc-300 before:rounded-full">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold text-[#0B0B16] mb-2 flex items-center gap-2">
                          <Clock size={18} className="text-[#6F00FF]" /> Typical Timeline
                        </h4>
                        <p className="text-[#5A5A6E] text-sm">{steps[activeStep].timeline}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0B0B16] mb-2 flex items-center gap-2">
                          <Users size={18} className="text-[#6F00FF]" /> Team Involved
                        </h4>
                        <p className="text-[#5A5A6E] text-sm">{steps[activeStep].team}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Featured Work */}
      <section id="work" className="py-24 bg-[#FAFAFB] border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-[#0B0B16]">Selected work</h2>
              <p className="text-[#5A5A6E] text-lg font-body">Real client products we have helped design and engineer.</p>
            </div>
            <a href="#contact">
              <Button variant="outline" className="rounded-full bg-white text-[#0B0B16] border-zinc-200 hover:bg-zinc-50 shrink-0">
                Discuss your project
              </Button>
            </a>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Large Case Study */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 group rounded-3xl overflow-hidden bg-white border border-zinc-200 hover:shadow-xl hover:border-[#6F00FF]/20 transition-all cursor-pointer flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-zinc-100 relative">
                <img 
                  src={projects[0].coverImage} 
                  alt={projects[0].title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-90 group-hover:opacity-100" 
                />
                <div className="absolute top-6 left-6 flex gap-2">
                  <Badge className="bg-white/90 backdrop-blur text-[#0B0B16] font-semibold border-0">
                    {projects[0].category}
                  </Badge>
                </div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <div className="text-sm font-semibold text-[#6F00FF] mb-2">{projects[0].client}</div>
                <h3 className="text-3xl font-display font-bold mb-4 text-[#0B0B16] group-hover:text-[#6F00FF] transition-colors">{projects[0].title}</h3>
                <p className="text-[#5A5A6E] mb-8">{projects[0].description}</p>
                <div className="mt-auto pt-6 border-t border-zinc-100 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex gap-2 flex-wrap">
                    {projects[0].tech.slice(0,3).map(t => (
                      <span key={t} className="text-xs font-medium bg-zinc-100 px-3 py-1 rounded-full text-zinc-600">{t}</span>
                    ))}
                  </div>
                  {projects[0].url ? (
                    <a href={projects[0].url} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#6F00FF] font-semibold text-sm">
                      Visit site <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </a>
                  ) : null}
                </div>
              </div>
            </motion.div>

            {/* Two Smaller Case Studies */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              {projects.slice(1, 3).map((project, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * (i+1) }}
                  className="group rounded-3xl overflow-hidden bg-white border border-zinc-200 hover:shadow-xl hover:border-[#6F00FF]/20 transition-all cursor-pointer flex flex-col sm:flex-row h-full"
                >
                  <div className="sm:w-2/5 aspect-square sm:aspect-auto overflow-hidden bg-zinc-100 relative">
                    <img 
                      src={project.coverImage} 
                      alt={project.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-90 group-hover:opacity-100" 
                    />
                  </div>
                  <div className="p-6 sm:w-3/5 flex flex-col justify-center">
                    <div className="text-xs font-semibold text-[#6F00FF] mb-2">{project.client}</div>
                    <h3 className="text-xl font-display font-bold mb-3 text-[#0B0B16] group-hover:text-[#6F00FF] transition-colors">{project.title}</h3>
                    <div className="text-sm font-bold text-emerald-600 mb-4 bg-emerald-50 inline-block px-3 py-1 rounded-md self-start border border-emerald-100">
                      {project.results[0]}
                    </div>
                    {project.url ? (
                      <a href={project.url} target="_blank" rel="noopener noreferrer" className="mt-auto flex items-center text-[#5A5A6E] group-hover:text-[#6F00FF] font-medium text-sm transition-colors">
                        Visit site <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : null}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 7. Tech stack marquee */}
      <section className="py-16 bg-white overflow-hidden border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-widest">Technologies we master</h3>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex overflow-hidden w-full mask-edges relative">
            <motion.div 
              className="flex gap-4 whitespace-nowrap min-w-max"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, ease: "linear", duration: 40 }}
            >
              {["React", "Next.js", "TypeScript", "Node.js", "Python", "Django", "Flutter", "Swift", "Kotlin", "AWS", "React", "Next.js", "TypeScript", "Node.js", "Python", "Django", "Flutter", "Swift", "Kotlin", "AWS"].map((tech, i) => (
                <div key={i} className="px-6 py-3 bg-[#FAFAFB] border border-zinc-200 rounded-full text-[#0B0B16] font-semibold text-sm shadow-sm">
                  {tech}
                </div>
              ))}
            </motion.div>
          </div>
          <div className="flex overflow-hidden w-full mask-edges relative">
            <motion.div 
              className="flex gap-4 whitespace-nowrap min-w-max"
              animate={{ x: ["-50%", "0%"] }}
              transition={{ repeat: Infinity, ease: "linear", duration: 35 }}
            >
              {["GCP", "Docker", "Kubernetes", "PostgreSQL", "MongoDB", "Redis", "TensorFlow", "PyTorch", "Solidity", "GraphQL", "GCP", "Docker", "Kubernetes", "PostgreSQL", "MongoDB", "Redis", "TensorFlow", "PyTorch", "Solidity", "GraphQL"].map((tech, i) => (
                <div key={i} className="px-6 py-3 bg-[#FAFAFB] border border-zinc-200 rounded-full text-[#0B0B16] font-semibold text-sm shadow-sm">
                  {tech}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <TeamSection />
      <TestimonialsSection />

      {/* 9. Pricing / Engagement models */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#0B0B16] mb-6">Engagement Models</h2>
            <p className="text-[#5A5A6E] text-lg font-body">Flexible structures designed to fit your project phase and organizational needs.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto">
            {/* Model 1 */}
            <div className="bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2">Project-Based</h3>
              <p className="text-sm text-[#5A5A6E] mb-6">Best for clear scopes and defined outcomes.</p>
              <div className="mb-8">
                <span className="text-4xl font-display font-black text-[#0B0B16]">From $25k</span>
              </div>
              <ul className="space-y-4 mb-8">
                {["Fixed price & timeline", "Dedicated project manager", "Full discovery phase", "Weekly progress reports", "30-day post-launch support"].map((b,i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-zinc-700">
                    <CheckCircle2 size={18} className="text-zinc-300 shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="w-full rounded-full h-12 bg-white text-[#0B0B16] border-zinc-200 hover:bg-zinc-50">Discuss Project</Button>
            </div>

            {/* Model 2 (Highlighted) */}
            <div className="bg-white border-2 border-[#6F00FF] rounded-3xl p-8 relative shadow-2xl scale-105 z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#6F00FF] text-white px-4 py-1 rounded-full text-xs font-bold tracking-widest uppercase shadow-md whitespace-nowrap">
                Most Popular
              </div>
              <div className="absolute inset-0 bg-gradient-to-b from-[#6F00FF]/5 to-transparent rounded-3xl pointer-events-none" />
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2 relative z-10">Dedicated Team</h3>
              <p className="text-sm text-[#5A5A6E] mb-6 relative z-10">Best for ongoing product development.</p>
              <div className="mb-8 relative z-10">
                <span className="text-4xl font-display font-black text-[#0B0B16]">$15k</span>
                <span className="text-[#5A5A6E] font-medium">/mo/engineer</span>
              </div>
              <ul className="space-y-4 mb-8 relative z-10">
                {["Full-time senior engineers", "Integrated with your tools", "Flexible roadmap", "Direct Slack access", "Scale up/down monthly", "Zero recruitment overhead"].map((b,i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-[#0B0B16]">
                    <CheckCircle2 size={18} className="text-[#6F00FF] shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
              <Button className="w-full rounded-full h-12 bg-[#6F00FF] hover:bg-[#5D21D2] text-white relative z-10 shadow-[0_8px_20px_rgb(111,0,255,0.2)]">Assemble Team</Button>
            </div>

            {/* Model 3 */}
            <div className="bg-[#FAFAFB] border border-zinc-200 rounded-3xl p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-display font-bold text-[#0B0B16] mb-2">Staff Augmentation</h3>
              <p className="text-sm text-[#5A5A6E] mb-6">Best for filling specific skill gaps quickly.</p>
              <div className="mb-8">
                <span className="text-4xl font-display font-black text-[#0B0B16]">Hourly</span>
              </div>
              <ul className="space-y-4 mb-8">
                {["Specific technical expertise", "Part-time or full-time", "Fast onboarding", "No long-term lock-in", "Vetted senior talent"].map((b,i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-zinc-700">
                    <CheckCircle2 size={18} className="text-zinc-300 shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="w-full rounded-full h-12 bg-white text-[#0B0B16] border-zinc-200 hover:bg-zinc-50">View Rates</Button>
            </div>
          </div>
        </div>
      </section>

      {/* 9.5 Estimator */}
      <section className="py-24 bg-[#FAFAFB] border-t border-zinc-200 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto px-6"
        >
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#0B0B16] mb-4">Estimate your project</h2>
            <p className="text-lg text-[#5A5A6E] font-body max-w-2xl">Get an instant cost and timeline estimate. No commitment, no email required.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left Col - Inputs */}
            <div className="lg:col-span-3 space-y-12">
              
              {/* Project Type */}
              <div>
                <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">1</span> 
                  Project Type
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {projectTypes.map((type, i) => {
                    const isSelected = estimatorProjectType === type.id;
                    return (
                      <div 
                        key={type.id} 
                        onClick={() => setEstimatorProjectType(type.id)}
                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${isSelected ? 'border-[#6F00FF] bg-[#6F00FF]/5' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                      >
                        <type.icon className={`w-6 h-6 mb-3 ${isSelected ? 'text-[#6F00FF]' : 'text-zinc-500'}`} />
                        <div className={`font-semibold mb-1 ${isSelected ? 'text-[#0B0B16]' : 'text-[#0B0B16]'}`}>{type.title}</div>
                        <div className="text-sm text-[#5A5A6E]">{type.desc}</div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Platforms (Conditional) */}
              {estimatorProjectType === 2 && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden">
                  <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">2</span> 
                    Platforms
                  </h3>
                  <div className="flex gap-3 flex-wrap">
                    {['iOS', 'Android', 'Web'].map(p => {
                      const isSelected = estimatorPlatforms.includes(p);
                      return (
                        <div 
                          key={p}
                          onClick={() => togglePlatform(p)}
                          className={`px-5 py-2 rounded-full border-2 cursor-pointer font-semibold transition-all select-none ${isSelected ? 'border-[#6F00FF] bg-[#6F00FF] text-white shadow-md' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'}`}
                        >
                          {p}
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}

              {/* Features */}
              <div>
                <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">{estimatorProjectType === 2 ? '3' : '2'}</span> 
                  Features
                </h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {featureOptions.map(f => {
                    const isSelected = estimatorFeatures.includes(f.id);
                    return (
                      <div 
                        key={f.id}
                        onClick={() => toggleFeature(f.id)}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer select-none ${isSelected ? 'border-[#6F00FF] bg-white shadow-md -translate-y-0.5' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                      >
                        <div className={`flex items-center justify-center w-5 h-5 rounded border ${isSelected ? 'border-[#6F00FF] bg-[#6F00FF] text-white' : 'border-zinc-300 bg-zinc-50'}`}>
                          {isSelected && <CheckCircle2 size={14} className="stroke-[3]" />}
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-[#0B0B16]' : 'text-zinc-600'}`}>{f.label}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Design Complexity */}
              <div>
                <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">{estimatorProjectType === 2 ? '4' : '3'}</span> 
                  Design Complexity
                </h3>
                <div className="flex bg-zinc-100 p-1.5 rounded-xl">
                  {designOptions.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setEstimatorDesign(i)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${estimatorDesign === i ? 'bg-white text-[#0B0B16] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Team Size */}
              <div>
                <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">{estimatorProjectType === 2 ? '5' : '4'}</span> 
                  Team Size
                  <span className="ml-auto text-sm text-[#6F00FF] bg-[#6F00FF]/10 px-2 py-0.5 rounded-md font-bold">{estimatorTeamSize[0]} Engineers</span>
                </h3>
                <div className="px-2 pt-2 pb-6">
                  <Slider 
                    value={estimatorTeamSize} 
                    onValueChange={setEstimatorTeamSize} 
                    min={1} 
                    max={8} 
                    step={1}
                    className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-[#6F00FF] [&_[role=slider]]:bg-white [&_.bg-primary]:bg-[#6F00FF]"
                  />
                </div>
              </div>

              {/* Timeline Urgency */}
              <div>
                <h3 className="text-lg font-semibold text-[#0B0B16] mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#6F00FF]/10 text-[#6F00FF] text-xs">{estimatorProjectType === 2 ? '6' : '5'}</span> 
                  Timeline Urgency
                </h3>
                <div className="flex bg-zinc-100 p-1.5 rounded-xl">
                  {urgencyOptions.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setEstimatorUrgency(i)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${estimatorUrgency === i ? 'bg-white text-[#0B0B16] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

            </div>

            {/* Right Col - Result Panel */}
            <div className="lg:col-span-2 lg:sticky lg:top-24">
              <div className="rounded-[2rem] p-1 bg-gradient-to-br from-[#6F00FF] via-[#9333EA] to-pink-500 shadow-2xl shadow-purple-500/20">
                <div className="bg-white rounded-[1.75rem] p-8 h-full flex flex-col relative overflow-hidden">
                  {/* bg glow */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#6F00FF]/5 rounded-full blur-[60px] pointer-events-none" />
                  
                  <div className="relative z-10">
                    <p className="mb-3 text-sm font-bold uppercase tracking-wider text-[#6F00FF]">Estimated Investment</p>

                    <div className="mb-4 min-h-[3.5rem]">
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={estimatedTotal}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.2 }}
                          className="font-display text-3xl font-black leading-tight tracking-tight text-[#0B0B16] sm:text-4xl lg:text-[2.5rem]"
                        >
                          ${Math.round(estimatedTotal * 0.92).toLocaleString()} – ${Math.round(estimatedTotal * 1.18).toLocaleString()}
                        </motion.p>
                      </AnimatePresence>
                    </div>

                    <div className="mb-8 inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
                      <Clock size={16} className="shrink-0" />
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={estimatedWeeks}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {(() => {
                            const low = Math.max(1, Math.floor(estimatedWeeks * 0.85));
                            const high = Math.max(low + 1, Math.ceil(estimatedWeeks * 1.15));
                            return `${low}–${high} weeks to launch`;
                          })()}
                        </motion.span>
                      </AnimatePresence>
                    </div>

                    <div className="border-t border-zinc-100 py-6">
                      <div className="space-y-4">
                        {[
                          { label: "Discovery & Design", pct: 0.15, color: "bg-purple-500" },
                          { label: "Development", pct: 0.60, color: "bg-blue-500" },
                          { label: "QA & Testing", pct: 0.15, color: "bg-emerald-500" },
                          { label: "Project Mgmt", pct: 0.10, color: "bg-amber-500" },
                        ].map((b, i) => {
                          const val = estimatedTotal * b.pct;
                          return (
                            <div key={i}>
                              <div className="flex justify-between text-xs mb-1.5">
                                <span className="font-medium text-zinc-600">{b.label}</span>
                                <span className="font-semibold text-[#0B0B16]">${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                              </div>
                              <div className="w-full bg-zinc-100 h-2 rounded-full overflow-hidden">
                                <motion.div 
                                  className={`h-full ${b.color}`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${b.pct * 100}%` }}
                                  transition={{ duration: 0.5, delay: i * 0.1 }}
                                />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className="border-t border-zinc-100 py-6">
                      <p className="text-sm font-bold text-[#0B0B16] mb-4">What's included:</p>
                      <ul className="space-y-3">
                        {["Senior-led team with delivery support", "Daily standups & transparent Jira", "Weekly sprint demos", "30-day post-launch support"].map((item, idx) => (
                          <li key={idx} className="text-sm text-zinc-600 flex items-start gap-2">
                            <CheckCircle2 size={16} className="text-[#6F00FF] shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="space-y-3">
                      <Button className="w-full h-14 rounded-xl bg-[#0B0B16] hover:bg-zinc-800 text-white font-bold shadow-lg text-base">
                        Book a discovery call
                      </Button>
                      <Button variant="ghost" className="w-full h-14 rounded-xl text-[#0B0B16] font-semibold hover:bg-zinc-50 border border-transparent hover:border-zinc-200">
                        <Mail className="mr-2 w-4 h-4" /> Email me this estimate
                      </Button>
                    </div>
                    
                    <p className="text-center text-xs text-zinc-400 mt-4 leading-relaxed">
                      Estimates are indicative. Final scope confirmed after a free 30-min discovery call.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 10. Trust principles */}
      <section className="py-12 bg-[#FAFAFB] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-sm font-semibold text-zinc-400 uppercase tracking-widest mb-6">How we earn trust</p>
          <div className="flex flex-wrap justify-center gap-4">
            {["Transparent scoping", "Senior-led delivery", "Full code ownership", "Weekly progress updates", "No vendor lock-in", "Honest timelines"].map((principle, i) => (
              <div key={i} className="px-5 py-2.5 bg-white border border-zinc-200 rounded-full flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 transition-colors cursor-default shadow-sm">
                <CheckCircle2 size={16} className="text-[#6F00FF]" /> {principle}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />

      {/* 12. Final CTA banner */}
      <section className="py-24 relative overflow-hidden bg-[#6F00FF] mx-6 mb-6 rounded-[3rem]">
        {/* Animated Gradient Mesh Background */}
        <div className="absolute inset-0 opacity-40 mix-blend-screen">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[140%] rounded-full bg-gradient-to-r from-blue-500 to-transparent blur-[100px] animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[140%] rounded-full bg-gradient-to-l from-purple-500 to-transparent blur-[100px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-display font-black text-white mb-8 tracking-tight">Have an idea?<br/>Let's build it together.</h2>
          <p className="text-white/80 text-xl mb-12 max-w-2xl mx-auto font-medium">Stop delaying your roadmap. Get a dedicated engineering team shipping features in less than 2 weeks.</p>
          
          <form className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" onSubmit={(e) => e.preventDefault()}>
            <Input 
              type="email" 
              placeholder="Enter your work email" 
              className="h-14 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/60 focus-visible:ring-white/30 px-6 text-lg"
            />
            <Button className="h-14 px-8 rounded-full bg-white text-[#6F00FF] hover:bg-zinc-100 font-bold shrink-0 text-base shadow-lg">
              Get in touch
            </Button>
          </form>
          <p className="mt-6 text-sm text-white/60 font-medium">We'll get back to you within 24 hours.</p>
        </div>
      </section>

    </Layout>
  );
}



